const AGE_RANGE_MIN = 0;
const AGE_RANGE_MAX = 100;
import { z } from 'zod';

export const configurationSchema = z.object({
  config_title: z.string().min(1, 'Title is required'),
  config_value: z.string().min(1, 'Value is required')
});

// Define the schema with custom username validation
export const adminUserSchema = z
  .object({
    role_id: z
      .number({ required_error: 'Role is required' })
      .min(1, 'Role is required'),
    name: z
      .string()
      .min(2, 'Name must be at least 2 characters')
      .max(50, 'Name must be less than 50 characters'),
    username: z
      .string()
      .min(3, 'Username must be at least 3 characters')
      .max(20, 'Username must be less than 20 characters'),
    email: z.string().email('Please enter a valid email address'),
    image: z.string().optional(),
    contact_number: z
      .string()
      .regex(/^\+?\d{1,15}$/, 'Please enter a valid phone number')
      .optional()
      .or(z.literal('')),
    password: z
      .string()
      .min(6, 'Password must be at least 6 characters')
      .optional()
      .or(z.literal('')),
    confirmPassword: z.string().optional().or(z.literal('')),
    address: z
      .string()
      .max(150, 'Address must be less than 150 characters')
      .optional()
      .or(z.literal('')),
    status: z
      .number({ required_error: 'Status is required' })
      .refine(val => [0, 1].includes(val), {
        message: 'Status must be Active or Inactive'
      })
  })
  .refine(
    data => {
      // If password is provided, confirmPassword must match it
      if (data.password && data.password !== '') {
        return data.confirmPassword === data.password;
      }
      // If password is empty, confirmPassword should also be empty
      return data.confirmPassword === '' || data.confirmPassword === undefined;
    },
    {
      message: "Passwords don't match",
      path: ['confirmPassword']
    }
  );

export type AdminUserFormData = z.infer<typeof adminUserSchema>;

export const serviceSchema = z
  .object({
    code: z
      .string()
      .min(1, 'Code is required')
      .max(5, 'Code must be 5 characters or less'),
    revenue_type: z.string().min(1, 'Revenue type is required'),
    sort_order: z.string().min(1, 'Sort order is required'),
    description: z.string().min(1, 'Description is required'),
    hours: z
      .union([z.string(), z.number()])
      .transform(val => {
        if (typeof val === 'string') {
          if (val.trim() === '') return undefined;
          return parseFloat(val);
        }
        return val;
      })
      .pipe(
        z
          .number({ required_error: 'Hours is required' })
          .refine(val => val > 0, { message: 'Hours must be greater than 0' })
          .refine(val => val <= 14, { message: 'Hours cannot exceed 14' })
      ),
    cost: z
      .string()
      .min(1, 'Cost is required')
      .max(8, 'Cost must be less than or equal to 99999999'),
    color: z.string().min(1, 'Color is required'),
    status: z
      .number({ required_error: 'Status is required' })
      .refine(val => [0, 1].includes(val), {
        message: 'Status must be Active or Inactive'
      }),
    finance_report: z.string().min(1, 'Finance report is required'),
    online_booking: z.string().min(1, 'Online booking is required'),
    service_mode: z.string().optional().or(z.literal('')),
    sort_order_report: z.string().min(1, 'Sort order report is required'),
    period_start: z.string().min(1, 'Period start date is required'),
    period_end: z.string().min(1, 'Period end date is required'),
    age_range_start: z
      .number({ required_error: 'Minimum age is required' })
      .min(AGE_RANGE_MIN, `Minimum age must be at least ${AGE_RANGE_MIN}`)
      .max(AGE_RANGE_MAX, `Minimum age cannot exceed ${AGE_RANGE_MAX}`),
    age_range_end: z
      .number({ required_error: 'Maximum age is required' })
      .min(AGE_RANGE_MIN, `Maximum age must be at least ${AGE_RANGE_MIN}`)
      .max(AGE_RANGE_MAX, `Maximum age cannot exceed ${AGE_RANGE_MAX}`),
    lesson_code_type: z.string().min(1, 'Lesson code type is required')
  })
  // Cross-field date validation: start must be on/before end
  .refine(
    data => {
      if (!data.period_start || !data.period_end) return true;
      return new Date(data.period_start) <= new Date(data.period_end);
    },
    {
      message: 'Period start date cannot be after period end date',
      path: ['period_start']
    }
  )
  .refine(
    data => {
      if (
        typeof data.age_range_start !== 'number' ||
        typeof data.age_range_end !== 'number'
      ) {
        return true;
      }
      return data.age_range_start <= data.age_range_end;
    },
    {
      message: 'Minimum age cannot exceed maximum age',
      path: ['age_range_end']
    }
  )
  // And end must be on/after start
  .refine(
    data => {
      if (!data.period_start || !data.period_end) return true;
      return new Date(data.period_end) >= new Date(data.period_start);
    },
    {
      message: 'Period end date cannot be before period start date',
      path: ['period_end']
    }
  );

export type ServiceFormData = z.infer<typeof serviceSchema>;

export const mountainSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  status: z
    .number({ required_error: 'Status is required' })
    .refine(val => [0, 1].includes(val), {
      message: 'Status must be Active or Inactive'
    }),
  default: z.union([z.literal(0), z.literal(1)]).optional()
});

export type MountainFormData = z.infer<typeof mountainSchema>;

export const addHotelManagerSchema = z.object({
  type: z
    .string({ required_error: 'Type is required' })
    .min(1, 'Type is required')
    .refine(val => ['hotel', 'agent'].includes(val), {
      message: 'Type must be either "hotel" or "agent"'
    }),
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  email: z
    .string()
    .email('Please enter a valid email address')
    .optional()
    .nullable()
    .or(z.literal('')),
  contact_name: z.string().max(100, 'Contact name is too long').nullable(),
  phone: z
    .string()
    .regex(/^[+]?[\d\s-()]*$/, 'Please enter a valid phone number')
    .max(20, 'Phone number is too long')
    .nullable(),
  sort_order: z
    .union([
      z.string().transform(val => (val === '' ? undefined : parseInt(val, 10))),
      z.number(),
      z.undefined(),
      z.null()
    ])
    .optional()
    .refine(
      val =>
        val === undefined ||
        val === null ||
        (Number.isInteger(val) && val >= 0),
      {
        message: 'Sort order must be a non-negative integer'
      }
    ),
  comments: z.string().max(250, 'Maximum 250 characters').nullable(),
  google_map_link: z
    .string()
    .nullable()
    .transform(v => v ?? '')
    .refine(
      v =>
        v === '' ||
        /^https:\/\/(www\.google\.com\/maps|maps\.app\.goo\.gl)\/.+$/i.test(v),
      { message: 'Enter a valid Google Maps URL' }
    ),
  status: z
    .number({ required_error: 'Status is required' })
    .refine(val => [0, 1].includes(val), {
      message: 'Status must be Active or Inactive'
    }),
  rate_level: z
    .union([
      z.string().transform(val => (val === '' ? undefined : parseInt(val, 10))),
      z
        .number({ required_error: 'Rate level is required' })
        .min(1, 'Rate level is required'),
      z.undefined()
    ])
    .refine(
      val =>
        val === undefined || (Number.isInteger(val) && val >= 1 && val <= 20),
      {
        message: 'Rate level must be a number between 1 and 20'
      }
    )
    .refine(val => val !== undefined, {
      message: 'Rate level is required'
    }),
  online_booking: z
    .string({ required_error: 'Online booking selection is required' })
    .min(1, 'Online booking selection is required')
    .refine(val => ['Y', 'N'].includes(val), {
      message: 'Online booking must be either "Yes" or "No"'
    })
});

export const commissionRateSchema = z.object({
  level: z
    .union([z.string().transform(val => parseInt(val, 10)), z.number()])
    .refine(
      val => !isNaN(val) && Number.isInteger(val) && val >= 1 && val <= 20,
      { message: 'Commission rate level is required' }
    ),

  lesson_code_id: z
    .union([z.string().transform(val => parseInt(val, 10)), z.number()])
    .refine(val => !isNaN(val) && Number.isInteger(val) && val >= 1, {
      message: 'Service code is required'
    }),

  rate: z
    .string()
    .min(1, 'Rate is required')
    .regex(/^\d*\.?\d+$/, 'Rate must be a valid number (e.g., 10 or 10.5)')
    .optional()
    .or(z.literal(''))
});

export const addInstructorManagerSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
  email: z
    .string()
    .email('Please enter a valid email address')
    .optional()
    .nullable()
    .or(z.literal('')),
  address: z
    .string()
    .max(150, 'Address must be less than 150 characters')
    .optional()
    .or(z.literal('')),
  contact_number: z
    .string()
    .regex(/^\+?\d{1,15}$/, 'Please enter a valid phone number')
    .optional()
    .or(z.literal('')),
  image: z.string().optional(),
  rate_level: z
    .union([
      z.string().transform(val => (val === '' ? undefined : parseInt(val, 10))),
      z
        .number({ required_error: 'Rate level is required' })
        .min(1, 'Rate level is required'),
      z.undefined()
    ])
    .refine(
      val =>
        val === undefined || (Number.isInteger(val) && val >= 1 && val <= 20),
      {
        message: 'Rate level must be a number between 1 and 20'
      }
    )
    .refine(val => val !== undefined, {
      message: 'Rate level is required'
    }),
  tax_rate: z
    .number({ required_error: 'Tax rate is required' })
    .positive('Tax rate must be positive')
    .max(100, 'Tax rate cannot exceed 100'),
  days_off: z.array(z.number().min(1).max(7)).optional(),
  rank_type: z.enum(['SK', 'SB', 'O'], {
    required_error: 'Rank type is required'
  }),
  rank: z
    .number({ required_error: 'Rank is required' })
    .positive('Rank must be positive')
    .min(1, 'Rank must be between 1 and 9999')
    .max(9999, 'Rank must be between 1 and 9999'),
  status: z
    .number({ required_error: 'Status is required' })
    .refine(val => [0, 1].includes(val), {
      message: 'Status must be Active or Inactive'
    }),
  color: z.string().min(1, 'Color is required')
});

export type InstructorManagerFormData = z.infer<
  typeof addInstructorManagerSchema
>;

export const transportSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().min(1, 'Description is required'),
  status: z.union([z.literal(0), z.literal(1)]),
  transport: z
    .number({ required_error: 'Transport is required' })
    .refine(val => [0, 1].includes(val), {
      message: 'Status must be Active or Inactive'
    }),
  sort_order: z.string().optional().or(z.literal(''))
});

export type TransportFormData = z.infer<typeof transportSchema>;

export const instructorCommissionSchema = z.object({
  level: z
    .string()
    .min(1, 'Level is required')
    .regex(/^\d+$/, 'Level must be a valid number')
    .refine(val => {
      const num = parseInt(val, 10);
      return num >= 1 && num <= 20;
    }, 'Level must be between 1 and 20'),
  lesson_code_id: z
    .string()
    .min(1, 'Lesson Code ID is required')
    .regex(/^\d+$/, 'Lesson Code ID must be a valid number'),
  rate: z
    .string()
    .min(1, 'Rate is required')
    .regex(/^\d*\.?\d+$/, 'Rate must be a valid number (e.g., 10 or 10.5)')
    .optional()
    .or(z.literal(''))
});

export const addDietManagerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  sort_order: z.string().min(1, 'Sort order is required'),
  diet_type: z.string().min(1, 'Diet type is required'),
  status: z
    .number({ required_error: 'Status is required' })
    .refine(val => [0, 1].includes(val), {
      message: 'Status must be Active or Inactive'
    })
});

export const addStaffManagerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  date_of_birth: z.string().min(1, 'Date of birth is required'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().regex(/^\+?\d{1,15}$/, 'Please enter a valid phone number'),
  address_line_1: z.string().min(1, 'Address line 1 is required'),
  address_line_2: z.string().min(1, 'Address line 2 is required'),
  city: z.string().min(1, 'City is required'),
  zip_code: z.string().min(1, 'Zip code is required'),
  monthly_pay_rate: z.string().min(1, 'Monthly pay rate is required'),
  federal_tax: z.string().min(1, 'Federal tax is required'),
  health_insurance: z.string().min(1, 'Health insurance is required'),
  pension: z.string().min(1, 'Pension is required'),
  status: z
    .number({ required_error: 'Status is required' })
    .refine(val => [0, 1].includes(val), {
      message: 'Status must be Active or Inactive'
    })
});
// Update Transport modal schema
export const updateTransportModalSchema = z
  .object({
    car_id: z
      .union([z.string(), z.number()])
      .refine(v => v !== '' && v !== undefined, {
        message: 'Vehicle is required'
      }),
    pickup_time: z.string().min(1, 'Pickup time is required'),
    return_time: z.string().optional().or(z.literal('')),
    start: z.string().optional().or(z.literal('')),
    transport_pax: z
      .union([z.number(), z.undefined()])
      .transform(v => (typeof v === 'number' ? v : 0))
      .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
    transport_comments: z
      .string()
      .max(500, 'Maximum 500 characters')
      .optional()
      .or(z.literal(''))
  })
  .superRefine((val, ctx) => {
    const hasPickup = (val.pickup_time || '').length > 0;
    const hasReturn = (val.return_time || '').length > 0;
    if (hasPickup && !hasReturn) {
      ctx.addIssue({
        code: 'custom',
        message: 'Return time is required',
        path: ['return_time']
      });
    }
  });

export const scheduleLessonSchema = z.object({
  date: z.date({ required_error: 'Date is required' }),
  instructor_id: z
    .number({ required_error: 'Instructor is required' })
    .min(1, 'Instructor is required'),
  string: z
    .union([z.string(), z.number(), z.undefined(), z.null()])
    .transform(v => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && v.trim().length > 0) {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    })
    .refine(v => v >= 0 && v <= 9999, { message: 'Invalid string' }),
  client_name: z.string().min(1, 'Client is required'),
  lesson_code_id: z
    .number({ required_error: 'Service is required' })
    .min(1, 'Service is required'),
  am: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  pm: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  fd: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  transport_pax: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  ski_level: z.string().optional().or(z.literal('')),
  start: z.string().min(1, 'Start is required'),
  end: z.string().min(1, 'End is required'),
  // hotel_id: z.union([z.number(), z.undefined()]).optional(),
  hotel_id: z
    .union([z.number(), z.string(), z.undefined()])
    .optional()
    .transform(val => {
      if (typeof val === 'string') {
        if (val.trim() === '') return undefined;
        const num = Number(val);
        return Number.isFinite(num) ? num : undefined;
      }
      return val;
    }),
  car_id: z
    .union([z.number(), z.string(), z.undefined()])
    .optional()
    .transform(val => {
      if (typeof val === 'string') {
        if (val.trim() === '') return undefined;
        const num = Number(val);
        return Number.isFinite(num) ? num : undefined;
      }
      return val;
    }),
  pickup_time: z.string().optional().or(z.literal('')),
  mountain_id: z
    .union([z.number(), z.string(), z.undefined()])
    .optional()
    .transform(val => {
      if (typeof val === 'string') {
        if (val.trim() === '') return undefined;
        const num = Number(val);
        return Number.isFinite(num) ? num : undefined;
      }
      return val;
    }),
  agent_id: z
    .union([z.number(), z.string(), z.undefined()])
    .optional()
    .transform(val => {
      if (typeof val === 'string') {
        if (val.trim() === '') return undefined;
        const num = Number(val);
        return Number.isFinite(num) ? num : undefined;
      }
      return val;
    }),
  reference: z.string().optional().or(z.literal('')),
  paid: z.enum(['Y', 'N']).optional().default('N'),
  lesson_req: z.string().optional().or(z.literal('')),
  comments: z.string().optional().or(z.literal(''))
});

export const addLessonWizardSchema = z.object({
  date: z.date({ required_error: 'Date is required' }),
  instructor_id: z
    .number({ required_error: 'Instructor is required' })
    .min(1, 'Instructor is required'),
  string: z
    .union([z.string(), z.number(), z.undefined(), z.null()])
    .transform(v => {
      if (typeof v === 'number') return v;
      if (typeof v === 'string' && v.trim().length > 0) {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
      }
      return 0;
    })
    .refine(v => v >= 0 && v <= 9999, { message: 'Invalid string' }),
  client_name: z.string().min(1, 'Client is required'),
  lesson_code_id: z
    .number({ required_error: 'Service is required' })
    .min(1, 'Service is required'),
  am: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  pm: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  fd: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  transport_pax: z
    .union([z.number(), z.string(), z.undefined()])
    .transform(v =>
      typeof v === 'number'
        ? v
        : typeof v === 'string' && v.trim() !== ''
          ? Number(v)
          : 0
    )
    .refine(v => v >= 0 && v <= 99, { message: 'Max 99' }),
  ski_level: z.string().optional().or(z.literal('')),
  start: z.string().min(1, 'Start is required'),
  end: z.string().min(1, 'End is required'),
  hotel_id: z.union([z.number(), z.undefined()]).optional(),
  car_id: z.union([z.number(), z.undefined()]).optional(),
  pickup_time: z.string().optional().or(z.literal('')),
  mountain_id: z.union([z.number(), z.undefined()]).optional(),
  agent_id: z.union([z.number(), z.undefined()]).optional(),
  reference: z.string().optional().or(z.literal('')),
  paid: z.enum(['Y', 'N']).optional().default('N'),
  comments: z.string().optional().or(z.literal('')),
  lesson_req: z.string().optional().or(z.literal(''))
});
// Use a distinct exported type name to avoid clashing with interfaces
export type AddLessonWizardValues = z.infer<typeof addLessonWizardSchema>;
